import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Sun, Moon, Bell, ChevronDown, LogOut, MapPin, Check, Settings, Menu } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { FocusTrap } from 'focus-trap-react';
import { FOCUS_TRAP_OPTIONS } from '@/constants/focus-trap';
import { LogoutConfirmModal } from '@/components/ui/logout-confirm-modal';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/auth.store';
import { useAmbitoStore } from '@/stores/ambito.store';
import { useUnreadCount, useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/api/notifications';
import { cn } from '@/lib/utils';
import { timeAgo } from '@/lib/format';

import { useAmbitos, AMBITOS_FALLBACK } from '@/hooks/use-ambitos';
import type { AmbitoResponse } from '@sime/shared';

function getAmbitoLabel(id: number, ambitos: AmbitoResponse[]): string {
  const entry = ambitos.find((a) => a.id === id);
  return entry?.nombre ?? `Ambito ${id}`;
}

// --- Notification icon color by type ---
const NOTIF_TYPE_COLORS: Record<string, string> = {
  TICKET_APPROVED: 'var(--sime-success)',
  TICKET_REJECTED: 'var(--sime-danger)',
  EXCEL_UPLOAD_COMPLETE: 'var(--sime-accent)',
  EXCEL_UPLOAD_FAILED: 'var(--sime-danger)',
  CLOSURE_COMPLETE: 'var(--sime-success)',
  PACKAGE_READY: 'var(--sime-success)',
  LOTE_SENT: 'var(--sime-accent)',
  REMINDER_FIRMA: 'var(--sime-warning)',
  TICKET_ASSIGNED: 'var(--sime-accent)',
  TICKET_TRANSITIONED: 'var(--sime-accent)',
  APPROVAL_REQUESTED: 'var(--sime-warning)',
  BATCH_READY: 'var(--sime-accent)',
  SYSTEM_ALERT: 'var(--sime-warning)',
};

// --- Notification Dropdown ---

function NotificationDropdown({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: notifData, isLoading } = useNotifications(1, 10);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = notifData?.data.items ?? [];

  const handleNotifClick = (id: number) => {
    markRead.mutate(id);
    onClose();
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const handleViewAll = () => {
    onClose();
    void navigate('/notificaciones/preferencias');
  };

  if (!open) return null;

  return (
      <FocusTrap focusTrapOptions={FOCUS_TRAP_OPTIONS}>
      <div
        className={cn(
          'absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] md:w-96 max-h-[480px] overflow-hidden rounded-2xl',
          'bg-sime-surface/95 backdrop-blur-xl',
          'shadow-neu-xl',
          'border border-sime-border-subtle/30',
          'z-sime-dropdown',
          'animate-[dropdown-in_0.15s_ease-out]',
        )}
        role="dialog"
        aria-label="Panel de notificaciones"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-sime-border-subtle">
          <h3 className="text-[15px] font-semibold text-sime-text-primary">
            Notificaciones
          </h3>
          <button
            onClick={handleMarkAllRead}
            disabled={markAllRead.isPending || notifications.every((n) => n.read)}
            className="text-[13px] font-medium text-sime-accent hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
          >
            Marcar todas como leidas
          </button>
        </div>

        {/* List */}
        <div className="overflow-y-auto max-h-[360px]">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 bg-sime-surface-raised rounded-lg animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-3/4 bg-sime-surface-raised rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-sime-surface-raised rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <Bell className="h-8 w-8 mx-auto mb-3 text-sime-text-tertiary" />
              <p className="text-[13px] text-sime-text-tertiary">
                No tienes notificaciones
              </p>
            </div>
          ) : (
            <ul>
              {notifications.map((notif) => {
                const dotColor = NOTIF_TYPE_COLORS[notif.type] ?? 'var(--sime-accent)';
                return (
                  <li key={notif.id}>
                    <button
                      onClick={() => { handleNotifClick(notif.id); }}
                      className={cn(
                        'w-full text-left px-5 py-3.5 flex gap-3 transition-colors',
                        'hover:bg-sime-surface-raised',
                        !notif.read && 'bg-sime-accent/[0.03]',
                      )}
                    >
                      {/* Unread dot */}
                      <div className="pt-1.5 shrink-0">
                        <div
                          className={cn(
                            'w-2 h-2 rounded-full transition-opacity',
                            notif.read ? 'opacity-0' : 'opacity-100',
                          )}
                          style={{ backgroundColor: dotColor }}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          'text-[13px] leading-snug truncate',
                          notif.read
                            ? 'text-sime-text-secondary'
                            : 'text-sime-text-primary font-medium',
                        )}>
                          {notif.title}
                        </p>
                        <p className="text-[12px] text-sime-text-tertiary mt-0.5 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-[11px] text-sime-text-tertiary mt-1">
                          {timeAgo(notif.createdAt)}
                        </p>
                      </div>

                      {!notif.read ? (
                        <div className="pt-1 shrink-0">
                          <Check className="h-3.5 w-3.5 text-sime-text-tertiary" />
                        </div>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-sime-border-subtle px-5 py-3 flex items-center justify-between">
          <button
            onClick={handleViewAll}
            className="text-[13px] font-medium text-sime-accent hover:underline flex items-center gap-1.5"
          >
            <Settings className="h-3.5 w-3.5" />
            Preferencias
          </button>
        </div>
      </div>
      </FocusTrap>
  );
}

// --- Main Header ---

export function Header() {
  const { theme, toggleTheme, toggleMobileMenu } = useThemeStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { selectedAmbitoId, setSelectedAmbito } = useAmbitoStore();
  const [ambitoOpen, setAmbitoOpen] = useState(false);
  const ambitoRef = useRef<HTMLDivElement>(null);

  // Fetch ambitos from API
  const { data: ambitosData } = useAmbitos();
  // Fallback when the API returns nothing OR an empty array. The empty-array
  // case happens when a non-admin user's JWT carries an empty ambitoIds set
  // (e.g. cached JWT from before their ámbito was assigned). Without this
  // guard the user sees "Ambito 4" instead of "Baja Tensión" because the
  // canonical lookup fires against an empty list.
  const ambitos = ambitosData && ambitosData.length > 0 ? ambitosData : AMBITOS_FALLBACK;

  // Notification state
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.data.count ?? 0;

  const ambitoIds = useMemo(() => user?.ambitoIds ?? [], [user?.ambitoIds]);
  const hasMultipleAmbitos = ambitoIds.length > 1;

  // Validate selectedAmbitoId against current user's scope and auto-select when single ambito.
  // Defense in depth: if a stale ambito (e.g. from another user that survived rehydration)
  // is set, clear it instead of letting the API silently 403 or filter to the wrong scope.
  useEffect(() => {
    if (selectedAmbitoId !== null && !ambitoIds.includes(selectedAmbitoId)) {
      setSelectedAmbito(null);
      return;
    }
    const firstAmbito = ambitoIds[0];
    if (ambitoIds.length === 1 && firstAmbito !== undefined && selectedAmbitoId !== firstAmbito) {
      setSelectedAmbito(firstAmbito);
    }
  }, [ambitoIds, selectedAmbitoId, setSelectedAmbito]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ambitoRef.current && !ambitoRef.current.contains(e.target as Node)) {
        setAmbitoOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    if (ambitoOpen || notifOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }
  }, [ambitoOpen, notifOpen]);

  const handleAmbitoSelect = (id: number | null) => {
    setSelectedAmbito(id);
    setAmbitoOpen(false);
    void queryClient.invalidateQueries();
  };

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    logout();
    void navigate('/login', { replace: true });
  };

  const toggleNotifPanel = useCallback(() => {
    setNotifOpen((v) => !v);
    setAmbitoOpen(false);
  }, []);

  return (
    <header
      role="banner"
      className="h-16 bg-sime-surface/80 backdrop-blur-xl border-b border-sime-border-subtle/50 flex items-center justify-between px-4 md:px-8"
    >
      {/* Left: Hamburger (mobile) + Ambito selector */}
      <div className="flex items-center gap-3">
        {/* Hamburger menu button - mobile only */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-xl text-sime-text-secondary hover:text-sime-text-primary transition-colors"
          aria-label="Abrir menu de navegacion"
        >
          <Menu className="h-5 w-5" />
        </button>
        {ambitoIds.length > 0 ? (
          <div ref={ambitoRef} className="relative">
            {hasMultipleAmbitos ? (
              <button
                onClick={() => { setAmbitoOpen((v) => !v); }}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150',
                  'text-sime-text-primary hover:bg-sime-surface-raised',
                  'shadow-neu-btn-hover',
                )}
                aria-expanded={ambitoOpen}
                aria-haspopup="listbox"
                aria-label="Seleccionar ambito"
              >
                <MapPin className="h-4 w-4 text-sime-accent" />
                <span>{selectedAmbitoId ? getAmbitoLabel(selectedAmbitoId, ambitos) : 'Todos los ambitos'}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', ambitoOpen && 'rotate-180')} />
              </button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-sime-text-primary">
                <MapPin className="h-4 w-4 text-sime-accent" />
                <span>{ambitoIds[0] !== undefined ? getAmbitoLabel(ambitoIds[0], ambitos) : ''}</span>
              </div>
            )}

            {/* Dropdown */}
            {ambitoOpen && hasMultipleAmbitos ? (
              <ul
                role="listbox"
                aria-label="Ambitos disponibles"
                className="absolute top-full left-0 mt-2 w-56 rounded-xl py-2 bg-sime-surface shadow-neu-raised z-sime-dropdown"
              >
                <li>
                  <button
                    role="option"
                    aria-selected={selectedAmbitoId === null}
                    onClick={() => { handleAmbitoSelect(null); }}
                    className={cn(
                      'w-full text-left px-4 py-2.5 text-sm transition-colors',
                      selectedAmbitoId === null
                        ? 'text-sime-accent font-medium bg-sime-surface-raised'
                        : 'text-sime-text-secondary hover:text-sime-text-primary hover:bg-sime-surface-raised',
                    )}
                  >
                    Todos los ambitos
                  </button>
                </li>
                {ambitoIds.map((id) => (
                  <li key={id}>
                    <button
                      role="option"
                      aria-selected={selectedAmbitoId === id}
                      onClick={() => { handleAmbitoSelect(id); }}
                      className={cn(
                        'w-full text-left px-4 py-2.5 text-sm transition-colors',
                        selectedAmbitoId === id
                          ? 'text-sime-accent font-medium bg-sime-surface-raised'
                          : 'text-sime-text-secondary hover:text-sime-text-primary hover:bg-sime-surface-raised',
                      )}
                    >
                      {getAmbitoLabel(id, ambitos)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-4">
        {/* Notification bell with badge */}
        <div ref={notifRef} className="relative">
          <button
            onClick={toggleNotifPanel}
            className={cn(
              'relative p-2 rounded-xl transition-colors',
              notifOpen
                ? 'text-sime-accent bg-sime-accent/[0.08]'
                : 'text-sime-text-secondary hover:text-sime-text-primary',
            )}
            aria-label={`Notificaciones${unreadCount > 0 ? `, ${unreadCount} sin leer` : ''}`}
            aria-expanded={notifOpen}
            aria-haspopup="dialog"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 ? (
              <span
                key={unreadCount}
                className={cn(
                  'absolute -top-0.5 -right-0.5 flex items-center justify-center',
                  'min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white',
                  'bg-sime-danger',
                  'animate-[badge-pop_0.2s_ease-out]',
                )}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </button>

          <NotificationDropdown
            open={notifOpen}
            onClose={() => { setNotifOpen(false); }}
          />
        </div>

        <button
          onClick={toggleTheme}
          className="p-2 rounded-xl text-sime-text-secondary hover:text-sime-text-primary transition-colors"
          aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
        >
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </button>

        <div className="flex items-center gap-3 pl-4 border-l border-sime-border-subtle">
          <button
            onClick={() => { void navigate('/perfil'); }}
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Mi perfil"
          >
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-bold" style={{ background: 'var(--sime-gradient-1)' }}>
              {user?.nombre[0] ?? 'U'}
            </div>
            <span className="hidden md:inline text-sm text-sime-text-primary font-medium">
              {user?.nombre ?? 'Usuario'}
            </span>
          </button>
          <button
            onClick={() => { setShowLogoutConfirm(true); }}
            className="p-2 rounded-xl text-sime-text-tertiary hover:text-sime-danger transition-colors"
            aria-label="Cerrar sesion"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      <LogoutConfirmModal
        open={showLogoutConfirm}
        onClose={() => { setShowLogoutConfirm(false); }}
        onConfirm={handleLogout}
      />
    </header>
  );
}
